import { useMemo, useState } from 'react'

function orderFilterLabel(filter) {
  if (filter === 'payable') return 'Payment due'
  if (filter === 'weekly-hold') return 'Weekly hold'
  if (filter === 'shipped') return 'Shipped'
  if (filter === 'paid') return 'Paid'
  if (filter === 'relist') return 'Relist risk'
  return 'All orders'
}

export default function OrdersPanel({ rows = [], expanded = false, onPayOrder, onSectionChange }) {
  const [filter, setFilter] = useState('all')

  const summary = useMemo(() => {
    const dueNow = rows.filter((row) => row.payable).length
    const weeklyHold = rows.filter((row) => row.status === 'weekly hold').length
    const paid = rows.filter((row) => row.status === 'paid').length
    const shipped = rows.filter((row) => row.status === 'shipped').length

    return { dueNow, weeklyHold, paid, shipped }
  }, [rows])

  const visibleRows = useMemo(() => {
    const sorted = [...rows].sort((a, b) => {
      const aPayable = a.payable ? 0 : 1
      const bPayable = b.payable ? 0 : 1
      if (aPayable !== bPayable) return aPayable - bPayable

      const aDue = a.rawDueAt ? new Date(a.rawDueAt).getTime() : Number.POSITIVE_INFINITY
      const bDue = b.rawDueAt ? new Date(b.rawDueAt).getTime() : Number.POSITIVE_INFINITY
      if (aDue !== bDue) return aDue - bDue

      return String(a.item || '').localeCompare(String(b.item || ''))
    })

    if (filter === 'payable') return sorted.filter((row) => row.payable)
    if (filter === 'weekly-hold') return sorted.filter((row) => row.status === 'weekly hold')
    if (filter === 'paid') return sorted.filter((row) => row.status === 'paid')
    if (filter === 'shipped') return sorted.filter((row) => row.status === 'shipped')
    if (filter === 'relist') return sorted.filter((row) => row.stageTone === 'danger' || row.status === 'penalty applied' || row.status === 'relist queued')

    return sorted
  }, [filter, rows])

  const filterItems = [
    { id: 'all', label: 'All', count: rows.length },
    { id: 'payable', label: 'Payment due', count: summary.dueNow },
    { id: 'weekly-hold', label: 'Weekly hold', count: summary.weeklyHold },
    { id: 'paid', label: 'Paid', count: summary.paid },
    { id: 'shipped', label: 'Shipped', count: summary.shipped },
  ]

  return (
    <section className={`tactical-card obc-dashboard__orders obc-orders-page ${expanded ? 'is-expanded' : ''}`}>
      <div className="obc-dashboard__side-head">
        <div>
          <h3>Orders & Wins</h3>
          <p>Track auction wins, buy-now purchases, weekly holds, and payment status.</p>
        </div>
        <div className="obc-dashboard__section-actions">
          <button type="button" onClick={() => onSectionChange?.('orders')}>View all</button>
          <button type="button" onClick={() => setFilter((current) => (current === 'all' ? 'payable' : 'all'))}>
            {orderFilterLabel(filter)}
          </button>
        </div>
      </div>

      <div className="obc-orders-page__summary">
        <article>
          <strong>{summary.dueNow}</strong>
          <span>Payment due</span>
        </article>
        <article>
          <strong>{summary.weeklyHold}</strong>
          <span>Weekly hold</span>
        </article>
        <article>
          <strong>{summary.paid}</strong>
          <span>Paid</span>
        </article>
        <article>
          <strong>{summary.shipped}</strong>
          <span>Shipped</span>
        </article>
      </div>

      <div className="obc-orders-page__filters" role="tablist" aria-label="Order filters">
        {filterItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={filter === item.id ? 'is-active' : ''}
            onClick={() => setFilter(item.id)}
          >
            <span>{item.label}</span>
            <strong>{item.count}</strong>
          </button>
        ))}
      </div>

      <div className="obc-orders-page__list">
        {visibleRows.map((row) => (
          <article key={row.rawId || row.id}>
            <div className="obc-orders-page__head">
              <div>
                <span>{row.id}</span>
                <strong>{row.item}</strong>
                <small>{row.status} / {row.shipping}</small>
              </div>
              <strong>{row.total}</strong>
            </div>

            <div className="obc-orders-page__body">
              <span>
                <small>Status</small>
                <b className={`obc-order-stage is-${row.stageTone || 'neutral'}`}>{row.stage || row.status}</b>
              </span>
              <span>
                <small>Due</small>
                <strong>{row.due}</strong>
              </span>
              <span>
                <small>Tracking</small>
                <strong>{row.tracking}</strong>
              </span>
              <span>
                <small>Reminders</small>
                <strong>{row.reminders || 0}</strong>
              </span>
            </div>

            {expanded ? (
              <div className="obc-orders-page__details">
                <span><small>Penalty</small><strong>{row.penaltyAt || 'Not scheduled'}</strong></span>
                <span><small>Relist</small><strong>{row.relistAt || 'Not eligible'}</strong></span>
                <span><small>Action</small><strong>{row.action || 'Monitor'}</strong></span>
              </div>
            ) : null}

            <div className="obc-order-actions">
              <span>{row.payable ? 'Payment required' : row.action || 'Monitor'}</span>
              {row.payable ? <button type="button" onClick={() => onPayOrder?.(row)}>Pay now</button> : null}
            </div>
          </article>
        ))}
      </div>

      {!visibleRows.length ? (
        <p className="obc-dashboard__empty-state">
          {rows.length ? `No ${orderFilterLabel(filter).toLowerCase()} orders found.` : 'No orders or auction wins yet.'}
        </p>
      ) : null}
    </section>
  )
}
